import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useAuth} from './auth';
import {
  verificarIdpayAtiva,
  listarPendentesIdpay,
  CompraPendenteIdpay,
} from '../services/idpay';

type IdpayVerificationContextData = {
  ativa: boolean;
  pendencia: CompraPendenteIdpay | null;
  loading: boolean;
  refresh: () => Promise<void>;
  refreshOnce: () => Promise<void>;
  sheetShownRef: React.MutableRefObject<boolean>;
};

const IdpayVerificationContext = createContext<IdpayVerificationContextData>(
  {} as IdpayVerificationContextData,
);

export function IdpayVerificationProvider({children}: {children: React.ReactNode}) {
  const {isLogged} = useAuth();

  const [ativa, setAtiva] = useState(false);
  const [pendencia, setPendencia] = useState<CompraPendenteIdpay | null>(null);
  const [loading, setLoading] = useState(false);

  const requestIdRef = useRef(0);
  const loadedOnceRef = useRef(false);
  const sheetShownRef = useRef(false);

  useEffect(() => {
    if (!isLogged) {
      loadedOnceRef.current = false;
      sheetShownRef.current = false;
      setAtiva(false);
      setPendencia(null);
    }
  }, [isLogged]);

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const isStale = () => requestId !== requestIdRef.current;

    if (!isLogged) {
      setAtiva(false);
      setPendencia(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const {ativa_unico} = await verificarIdpayAtiva().catch(() => ({
        ativa_unico: false,
      }));

      if (isStale()) {
        return;
      }
      setAtiva(ativa_unico);

      if (!ativa_unico) {
        setPendencia(null);
        return;
      }

      const {compras} = await listarPendentesIdpay().catch(() => ({compras: []}));

      if (isStale()) {
        return;
      }

      const pendenteVeicular =
        compras.find(compra => compra.tipo_operacao === 'debito_veicular') ?? null;

      setPendencia(pendenteVeicular);
    } finally {
      if (!isStale()) {
        setLoading(false);
      }
    }
  }, [isLogged]);

  const loadOnce = useCallback(async () => {
    if (!isLogged || loadedOnceRef.current) {
      return;
    }
    loadedOnceRef.current = true;
    await load();
  }, [isLogged, load]);

  const value = useMemo(
    () => ({
      ativa,
      pendencia,
      loading,
      refresh: load,
      refreshOnce: loadOnce,
      sheetShownRef,
    }),
    [ativa, pendencia, loading, load, loadOnce],
  );

  return (
    <IdpayVerificationContext.Provider value={value}>
      {children}
    </IdpayVerificationContext.Provider>
  );
}

export function useIdpayVerification() {
  return useContext(IdpayVerificationContext);
}
